import { Component, OnInit, AfterViewInit, OnDestroy, NgZone, HostListener } from '@angular/core'; // Keep HostListener
import { SwirlService, ChatSession } from '../swirl.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatService } from '../chat.service';
import { Subscription } from 'rxjs';
import {RateLimitService} from "../../rate-limit.service";

@Component({
  selector: 'app-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss'],
  standalone: false
})
export class ChatHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  chatSessions: ChatSession[] = [];
  currentPage: number = 1;
  itemsPerPage: number = 10; // Still used for Chat pagination for Chats only
  loadingMore: boolean = false;
  allLoaded: boolean = false;
  activeChatId: number | null = null;
  activeMenuChatId: number | null = null;
  renamingChatId: number | null = null;
  renameInput: string = '';

  private activeChatSub!: Subscription;
  private pollResumeSubs = new Map<number, Subscription>();

  constructor(
    private swirlService: SwirlService,
    private zone: NgZone,
    private route: ActivatedRoute,
    public chatService: ChatService,
    private router: Router,
    private rl: RateLimitService
  ) {}

  ngOnInit(): void {
    this.loadChats();

    // Subscribe to route query params
    this.route.queryParams.subscribe(params => {
      const param = params['chat_id'];
      if (param !== undefined) {
        this.activeChatId = Number(param);
      }
    });

    // Subscribe to the active chat ID from ChatService.
    this.activeChatSub = this.chatService.activeChatId$.subscribe((id: number | null) => {
      if (id !== null) {
        this.activeChatId = id;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { chat_id: id },
          queryParamsHandling: 'merge'
        });
        setTimeout(() => {
          this.scrollActiveChatIntoView();
        }, 300);
      }
    });

    // Subscribe to updated Chat events (reordering).
    this.chatService.chatUpdated$.subscribe(update => {
      if (update) {
        const { chatId, updatedAt } = update;
        const idx = this.chatSessions.findIndex(c => c.id === chatId);
        if (idx !== -1) {
          if (updatedAt) {
            this.chatSessions[idx].date_updated = updatedAt;
          }
          const resurrectedChat = this.chatSessions[idx];
          this.chatSessions.splice(idx, 1);
          this.chatSessions.unshift(resurrectedChat);
        }
      }
    });

    // Listen for conversation started events
    this.chatService.isConversationStarted$.subscribe((started) => {
      if (started) {
        const currentId = this.chatService.getChatId();
        if (currentId === undefined || currentId === null) {
          this.reloadChats();
          return;
        }
        // If the chat already exists, we poll for a name
        const existingChat = this.chatSessions.find(chat => chat.id === currentId);
        if (existingChat) {
          setTimeout(() => {
            this.updateChatNameWithPolling(currentId);
          }, 2000);
        } else {
          // Insert a new chat immediately with a fallback
          const newChat: ChatSession = {
            id: currentId,
            owner: '',
            date_created: new Date().toISOString(),
            date_updated: new Date().toISOString(),
            subscribe: false,
            name: 'New Chat',
            display_name: 'New Chat',
            active: false,
            tags: [],
            ai_provider_id: null
          };
          this.chatSessions = [newChat, ...this.chatSessions];
          setTimeout(() => {
            this.updateChatNameWithPolling(currentId);
          }, 2000);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.zone.onStable.subscribe(() => {
      this.checkIfShouldPreload();
    });
  }

  loadMore(): void {
    this.loadChats();
  }

  private loadChats(): void {
    if (this.loadingMore || this.allLoaded) return;
    this.loadingMore = true;

    // The Chat endpoint is still paginated, but the MESSAGES endpoint is not.
    this.swirlService.getChats(this.currentPage, this.itemsPerPage).subscribe({
      next: response => {
        const chats = response.results || [];
        if (!chats.length || !response.next) {
          this.allLoaded = true;
        }

        // For each Chat, if there's no display_name, fetch earliest user message to set it
        chats.forEach((chat: ChatSession) => {
          if (!chat.display_name || chat.display_name.trim() === '') {
            // Non-paginated getMessages()
            this.swirlService.getMessages(chat.id).subscribe({
              next: messages => {
                // messages is a plain array (sorted ascending by the backend).
                const earliestMsg = messages[0];
                if (earliestMsg?.role === 'user') {
                  chat.display_name = earliestMsg.content;
                } else {
                  chat.display_name = 'New Chat';
                }
              },
              error: err => {
                console.error(`Error fetching messages for Chat ${chat.id}:`, err);
                chat.display_name = chat.display_name || '(No Chat name available)';
              }
            });
          }
        });

        this.chatSessions = [...this.chatSessions, ...chats];
        this.currentPage++;
        this.zone.onStable.subscribe(() => {
          this.checkIfShouldPreload();
        });
        this.loadingMore = false;
      },
      error: err => {
        console.error('Failed to get Chat sessions', err);
        this.loadingMore = false;
      }
    });
  }

  /**
   * Repeatedly fetch messages to see if a user message has arrived yet.
   */
  private updateChatNameWithPolling(chatId: number, retryCount: number = 0): void {
    // Pause polling while we’re throttled, and auto-resume when it clears
    if (this.rl.isThrottled) {
      if (!this.pollResumeSubs.has(chatId)) {
        const sub = this.rl.remaining$.subscribe(s => {
          if (s === 0) {
            sub.unsubscribe();
            this.pollResumeSubs.delete(chatId);
            this.updateChatNameWithPolling(chatId, retryCount); // pick up where we left off
          }
        });
        this.pollResumeSubs.set(chatId, sub);
      }
      return;
    }

    this.swirlService.getMessages(chatId).subscribe({
      next: (messages) => {
        const chat = this.chatSessions.find(c => c.id === chatId);
        if (chat) {
          // Find the first user message
          const firstUserMsg = messages.find(m => m.role === 'user');
          const newName = firstUserMsg?.content;
          if (newName && newName.trim() !== '') {
            // Update the local UI
            chat.display_name = newName;
            // Persist the update to the backend so that subsequent loads see the name.
            this.swirlService.updateChatDisplayName(chat.id, newName).subscribe({
              next: (res) => console.log('Persisted display_name update:', res),
              error: (err) => console.error('Error updating display_name on backend:', err)
            });
            return;  // Exit the polling function.
          } else if (retryCount < 5) {
            setTimeout(() => {
              this.updateChatNameWithPolling(chatId, retryCount + 1);
            }, 1000);
          } else if (
            !chat.display_name ||
            chat.display_name.trim() === '' ||
            chat.display_name === 'New Chat'
          ) {
            // After 5 retries, if still nothing, set a fallback.
            chat.display_name = '(No Chat name available)';
          }
        }
      },
      error: err => {
        console.error(`Error updating chat ${chatId} name:`, err);
        if (err?.status === 429) {
          // Start/refresh the shared cooldown from server hint and rely on the pause at top
          this.rl.startFromHeaders(err.headers, 10);
          return;
        }
        if (retryCount < 5) {
          setTimeout(() => {
            this.updateChatNameWithPolling(chatId, retryCount + 1);
          }, 1000);
        }
      }
    });
  }

  private reloadChats(): void {
    this.currentPage = 1;
    this.chatSessions = [];
    this.allLoaded = false;
    this.loadChats();
  }

  private checkIfShouldPreload(): void {
    const container = document.querySelector('.chat-history-container');
    if (
      container &&
      container.scrollHeight <= container.clientHeight + 10 &&
      !this.loadingMore &&
      !this.allLoaded
    ) {
      this.loadMore();
    }
  }

  /* Let the browser handle this display */
  private scrollActiveChatIntoView(): void {
    const container = document.querySelector('.chat-history-container') as HTMLElement;
    const active = container?.querySelector('.chat-history-li.active') as HTMLElement;
    if (container && active) {
      /* 'nearest' - scroll only if needed, minimal distance */
      active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  trackByChatId(index: number, chat: ChatSession): number {
    return chat.id;
  }

  // Helper to reposition a chat
  private moveChatToTop(chat: ChatSession) {
    const existingIndex = this.chatSessions.findIndex(c => c.id === chat.id);
    if (existingIndex !== -1) {
      this.chatSessions.splice(existingIndex, 1);
    }
    this.chatSessions.unshift(chat);
  }

  // Open the item menu
  openChatMenu(chat: ChatSession, event: Event): void {
    event.stopPropagation();
    this.activeMenuChatId = (this.activeMenuChatId === chat.id ? null : chat.id);
  }

  // HostListener for doc clicks to close menus
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    this.activeMenuChatId = null;
    // Reset rename state so that any open inline rename form closes as well.
    this.renamingChatId = null;
    this.renameInput = '';
  }

  openRenameChat(chat: ChatSession, event: Event): void {
    event.stopPropagation();
    this.renamingChatId = chat.id;
    this.renameInput = chat.display_name || '';  // Provide a default value.
    // this.activeMenuChatId = null;  // Optionally, close the menu.
  }

  saveRenameChat(chat: ChatSession, event: Event): void {
    event.stopPropagation();
    // Call the service to update display_name
    this.swirlService.updateChatDisplayName(chat.id, this.renameInput).subscribe({
      next: (response) => {
        console.log('Chat rename successful:', response);
        // Update the chat display name locally
        chat.display_name = this.renameInput;
        // Optionally, trigger any notifications or UI updates
        this.chatService.notifyChatUpdated(chat.id, new Date().toISOString());
        // Exit rename mode
        this.renamingChatId = null;
        this.renameInput = '';
      },
      error: (error) => {
        console.error('Error renaming chat:', error);
      }
    });
  }

  cancelRename(event: Event): void {
    event.stopPropagation();
    this.renamingChatId = null;
    this.renameInput = '';
  }

  deleteChat(chat: ChatSession, event: Event): void {
    event.stopPropagation();

    if (!confirm(`Are you sure you want to delete chat "${chat.display_name}"`)) {
      return;
    }

    this.swirlService.deleteChat(chat.id).subscribe({
      next: (response: any) => {
        console.log('Chat deleted successfully (204)', response);
        this.chatService.changeEraseEvent(true);
        this.reloadChats();
        this.activeMenuChatId = null;
      },
      error: err => {
        console.error('Error deleting chat:', err);
      }
    });
  }

  deleteAllChats(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  
    if (!confirm("Are you sure you want to delete ALL Chats?")) {
      return;
    }
  
    this.swirlService.deleteAllChats().subscribe({
      next: (response: any) => {
        // 1) clear the main chat pane (erase event)
        this.chatService.changeEraseEvent(true);
  
        // 2) reload the history list
        this.reloadChats();
  
        // 3) close any open menu in history
        this.activeMenuChatId = null;
  
        // 4) now show the result alert (after a tiny delay so step 1–3 paint)
        setTimeout(() => {
          const count = response.headers.get('X-Deleted-Count') ?? '0';
          alert(`${count} Chat(s) deleted.`);
        }, 50);
      },
      error: (err) => {
        console.error('Error deleting all Chats:', err);
        alert("Something went wrong while trying to delete Chats.");
      }
    });
  }

  ngOnDestroy(): void {
    if (this.activeChatSub) {
      this.activeChatSub.unsubscribe();
    }
    this.pollResumeSubs.forEach(s => s.unsubscribe());
    this.pollResumeSubs.clear();
  }
}
